import { memoize } from "@use/decorators";

import { ElementModel, ElementModelData } from "@courses-pm2/api/src/ts/models/ElementModel";
import { reduceRichTextNodeStandardPageCount, renderContentNodes, RichtextContentNode } from "@courses-pm2/api/src/ts/models/elements/RichtextModel";

/**
 * Specifies the desired length of the preview text, given in number of
 * characters.
 */
const DESIRED_PREVIEW_TEXT_LENGTH = 150;

/**
 * Specification of data needed to render a single grid column.
 */
export type GridColumn = {
    /**
     * Specifies the size of the column, relatively to the other columns in
     * the grid.
     *
     * For instance, if three columns exist with sizes 3-6-3, then the first and
     * last column should be 25% each and the middle one should be 50%.
     */
    size: number;

    /**
     * Contains the list of elements that should be rendered within this column
     * in the grid.
     */
    content: RichtextContentNode[];
};

/**
 * Specification of data needed to instantiate a new GridModel.
 */
type GridData = {
    [K in "columns"]: GridModel[K];
};

/**
 * Describes the content to be rendered for the element, so that it's made
 * easily available.
 */
export class GridModel extends ElementModel {
    /**
     * Contains the list of colums to render within this grid.
     */
    public readonly columns: GridColumn[];

    /**
     * Apply data given when a new model is being instantiated.
     */
    constructor(initial: GridData & ElementModelData) {
        super(initial);

        this.columns = initial.columns;
    }

    /**
     * Helper that'll transform the content of the desired column into React
     * nodes that can be rendered directly.
     */
    @memoize()
    public renderColumn(index: number): React.ReactNode[] {
        if (!this.engine) {
            throw new Error("GridModel.renderColumn(): No rendering engine provided to this model.");
        }

        return renderContentNodes(
            (
                this.columns[index]
                    ? this.columns[index].content
                    : undefined
            ),
            this.engine,
        );
    }

    /**
     * Helper that'll return an absolute url to a preview image that's
     * representative of the actual element content.
     */
    public async getPreviewImageUrl(): Promise<string | undefined> {
        for (const column of this.columns) {
            for (const element of column.content) {
                if (element && element instanceof ElementModel) {
                    const previewImageUrl = await element.getPreviewImageUrl();

                    if (previewImageUrl !== undefined) {
                        return previewImageUrl;
                    }
                }
            }
        }

        // If we get here, no image preview url is available for any of the
        // content within this grid
        return undefined;
    }

    /**
     * Helper that'll return a text extract from the component, that can be used
     * as a preview of sorts.
     */
    public getPreviewText(): string | undefined {
        const previewTexts: string[] = [];

        for (const column of this.columns) {
            for (const element of column.content) {
                if (element && element instanceof ElementModel) {
                    const previewText = (element.getPreviewText() || "").trim();

                    if (previewText !== undefined) {
                        previewTexts.push(previewText);
                    }
                }
            }
        }

        // If any preview texts were found, then combind and push them
        if (previewTexts.length > 0) {
            const previewText = previewTexts.join(" ");
            const cropAt = previewText.indexOf(" ", DESIRED_PREVIEW_TEXT_LENGTH);

            return (
                cropAt !== -1
                    ? `${previewText.substr(0, cropAt)}...`
                    : previewText
            );
        }

        // If we get here, no text extract is available for any of the content
        // within this grid
        return undefined;
    }

    /**
     * @inheritdoc
     *
     * Iterate over all columns in the grid, and accumulate their combined count
     * of standard pages.
     */
    // tslint:disable-next-line: completed-docs
    @memoize()
    public getStandardPageCount(): number {
        return this.columns.reduce(
            (previousValue, column) => previousValue + reduceRichTextNodeStandardPageCount(column.content),
            0,
        );
    }
}
